(() => {
  if (customElements.get('olea-legal')) return;

  const motionEnabled = () => document.body.classList.contains('animations-enabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const blockTags = new Set(['P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'HR', 'BR', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'DL', 'DT', 'DD']);
  const readingText = node => {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1 || node.matches('script,style,[data-legal-number],[hidden]')) return '';
    const text = Array.from(node.childNodes).map(readingText).join('');
    return blockTags.has(node.tagName) ? ` ${text} ` : text;
  };

  class OleaLegal extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.body = this.querySelector('.olea-legal__body');
      this.content = this.body?.querySelector('[data-legal-content]');
      this.toc = this.querySelector('legal-toc');
      this.links = [];
      this.headings = [];
      this.measureDocument();
      this.prepareHeadings();
      this.buildContents();
      this.querySelectorAll('[data-legal-print]').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => window.print(), { signal });
      });
      this.addEventListener('focusin', event => {
        const rail = event.target.closest('.olea-legal__article-grid,.olea-legal__product-rail,.olea-legal__navigation-links');
        if (!rail || !this.contains(rail) || rail.scrollWidth <= rail.clientWidth + 1) return;
        let item = event.target;
        while (item.parentElement && item.parentElement !== rail) item = item.parentElement;
        if (item.parentElement !== rail) return;
        const itemBounds = item.getBoundingClientRect();
        const railBounds = rail.getBoundingClientRect();
        if (itemBounds.left >= railBounds.left - 1 && itemBounds.right <= railBounds.right + 1) return;
        item.scrollIntoView({ inline: rail.classList.contains('olea-legal__navigation-links') ? 'nearest' : 'center', block: 'nearest', behavior: motionEnabled() ? 'smooth' : 'auto' });
      }, { signal });
      const schedule = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateActive(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      window.addEventListener('hashchange', () => this.followHash(), { signal });
      window.addEventListener('popstate', () => this.followHash(), { signal });
      this.updateActive();
      this.hashFrame = requestAnimationFrame(() => this.followHash());
    }

    measureDocument() {
      let words = 0;
      if (this.content) {
        words = readingText(this.content).replace(/\u00a0/g, ' ').trim().match(/\S+/gu)?.length || 0;
      }
      const minutes = words ? Math.max(1, Math.ceil(words / 200)) : 0;
      this.querySelectorAll('[data-legal-reading]').forEach(node => {
        node.hidden = !words;
        node.textContent = (this.dataset.readingTemplate || '__count__').replace('__count__', String(minutes));
      });
    }

    prepareHeadings() {
      if (!this.content) return;
      const meaningful = selector => Array.from(this.content.querySelectorAll(selector)).filter(heading => {
        const text = heading.querySelector('[data-legal-heading-text]')?.textContent || heading.textContent;
        return text.trim() && !heading.closest('[hidden]');
      });
      const h2 = meaningful('h2');
      const h3 = meaningful('h3');
      const headings = h2.length <= 1 && h3.length ? h3 : h2;
      const counts = new Map();
      document.querySelectorAll('[id]').forEach(node => counts.set(node.id, (counts.get(node.id) || 0) + 1));
      const assigned = new Set();
      this.headings = headings.map((heading, index) => {
        let text = heading.querySelector('[data-legal-heading-text]');
        if (!text) {
          text = document.createElement('span');
          text.className = 'olea-legal__heading-text';
          text.dataset.legalHeadingText = '';
          while (heading.firstChild) text.append(heading.firstChild);
          heading.append(text);
        }
        const label = text.textContent.trim();
        let id = heading.id;
        if (!id || /\s/.test(id) || counts.get(id) !== 1 || assigned.has(id)) {
          const base = `${this.body.id}-part-${index + 1}`;
          id = base;
          let suffix = 1;
          while (document.getElementById(id) || assigned.has(id)) id = `${base}-${++suffix}`;
          heading.id = id;
        }
        assigned.add(id);
        heading.dataset.legalHeading = '';
        if (!heading.querySelector('[data-legal-number]')) {
          const number = document.createElement('span');
          number.className = 'olea-legal__heading-number';
          number.dataset.legalNumber = '';
          number.setAttribute('aria-hidden', 'true');
          const desktop = document.createElement('span');
          desktop.className = 'olea-legal__number-desktop';
          desktop.textContent = String(index + 1).padStart(2, '0');
          const mobile = document.createElement('span');
          mobile.className = 'olea-legal__number-mobile';
          mobile.textContent = String(index + 1);
          number.append(desktop, mobile);
          heading.prepend(number);
        }
        return { element: heading, id, label };
      });
      this.querySelectorAll('[data-legal-section-count]').forEach(node => {
        node.hidden = !this.headings.length;
        node.textContent = this.partLabel(this.headings.length);
      });
    }

    partLabel(count) {
      const label = count === 1 ? this.dataset.partSingular : this.dataset.partPlural;
      return `${count} ${label || ''}`.trimEnd();
    }

    buildContents() {
      if (!this.toc) return;
      const list = this.toc.querySelector('ol');
      if (!list || !this.headings.length) { this.toc.hidden = true; return; }
      this.toc.hidden = false;
      this.links = this.headings.map((heading, index) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.dataset.legalTocLink = String(index);
        const number = document.createElement('span');
        number.className = 'olea-legal__toc-index';
        number.textContent = String(index + 1).padStart(2, '0');
        number.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.className = 'olea-legal__toc-text';
        label.textContent = heading.label;
        link.append(number, label);
        item.append(link);
        return { item, link };
      });
      list.replaceChildren(...this.links.map(value => value.item));
      const count = this.toc.querySelector('[data-legal-toc-count]');
      if (count) count.textContent = ` · ${this.partLabel(this.headings.length)}`;
      this.details = this.toc.querySelector('details');
      this.breakpoint = window.matchMedia('(min-width: 750px)');
      const setDetails = () => { if (this.details) this.details.open = this.breakpoint.matches; };
      setDetails();
      this.breakpoint.addEventListener?.('change', setDetails, { signal: this.controller.signal });
      this.toc.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('[data-legal-toc-link]');
        if (!link || !this.toc.contains(link)) return;
        const index = Number(link.dataset.legalTocLink);
        if (!this.headings[index]) return;
        event.preventDefault();
        const url = new URL(location.href);
        const hash = `#${encodeURIComponent(this.headings[index].id)}`;
        if (url.hash !== hash) {
          url.hash = hash;
          history.pushState({}, '', url.pathname + url.search + url.hash);
        }
        this.navigate(index, true, motionEnabled());
      }, { signal: this.controller.signal });
      this.toc.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const link = event.target.closest('[data-legal-toc-link]');
        if (!link || !this.toc.contains(link)) return;
        const index = Number(link.dataset.legalTocLink);
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.links.length - 1 : Math.max(0, Math.min(this.links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
        this.links[next]?.link.focus();
      }, { signal: this.controller.signal });
    }

    navigate(index, focus = false, smooth = false) {
      const heading = this.headings[index]?.element;
      if (!heading) return;
      this.activate(index);
      heading.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
      if (focus) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    followHash() {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const index = this.headings.findIndex(heading => heading.id === id);
      if (index >= 0) this.navigate(index);
    }

    activate(index) {
      this.links.forEach(({ link }, current) => {
        link.classList.toggle('is-active', current === index);
        if (current === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    updateActive() {
      const header = document.querySelector('.site-header');
      const headerBoundary = Math.max(0, header?.getBoundingClientRect().bottom || 0) + 32;
      const scrollPadding = Math.max(0, Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0);
      let index = 0;
      this.headings.forEach((heading, current) => {
        const scrollMargin = Math.max(0, Number.parseFloat(getComputedStyle(heading.element).scrollMarginTop) || 0);
        const boundary = Math.max(headerBoundary, scrollMargin + scrollPadding + 2);
        if (heading.element.getBoundingClientRect().top <= boundary) index = current;
      });
      this.activate(index);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.hashFrame);
      this.scrollFrame = null;
      this.hashFrame = null;
    }
  }

  customElements.define('olea-legal', OleaLegal);
})();
